import React from 'react'
import { shallowEqual } from 'react-redux'

import { SliceType } from '../../MCTextEditor/slice/types'
import { IProcessorState, ISlice, ITextProcessor } from '../processor/constants'
import { isRangeAIncludesB } from '../processor/range'

import { decorate } from './decorate'

type Decorator = Omit<ISlice, 'range'>

interface UseDecoratorsHook {
  (
    processor: ITextProcessor,
    edit?: (value: IProcessorState) => void,
    filterDecorators?: (s: ISlice) => boolean,
  ): {
    getList: () => Decorator[]
    toggle: (data: Partial<ISlice>) => void
    isActive: (type: SliceType) => boolean
    reset: () => void
  }
}

export type Decorators = ReturnType<UseDecoratorsHook>

const useDecorators: UseDecoratorsHook = (processor, edit, filterDecorators = () => true) => {
  const decoratorsJSONRef = React.useRef(JSON.stringify([]))
  const [decorators, setDecorators] = React.useState<Decorator[]>([])

  const selectedDecorators = getSelectedDecorators(processor, filterDecorators)
  const decoratorsJSON = JSON.stringify(selectedDecorators)
  if (decoratorsJSONRef.current !== decoratorsJSON) {
    decoratorsJSONRef.current = decoratorsJSON
    setDecorators(selectedDecorators)
  }

  const isActive = React.useCallback(
    (type: SliceType) => {
      return decorators.some((d) => d.type === type)
    },
    [decorators],
  )

  const toggle = React.useCallback(
    (data: Partial<ISlice>) => {
      const { type } = data
      if (!type) {
        return
      }

      const range = processor.getSelection()
      if (range.length === 0) {
        setDecorators(
          isActive(type as SliceType)
            ? decorators.filter((d) => d.type !== type)
            : [...decorators, data],
        )
        return
      }

      const slices = processor.getSlices()
      const existingSlice = slices.find((s) => s.type === type && isRangeAIncludesB(s.range, range))
      if (existingSlice) {
        processor.deleteSlice({ ...existingSlice, range })
      }
      if (!existingSlice || (data.data && !shallowEqual(data.data, existingSlice.data))) {
        decorate(processor, { range, ...data })
      }
      edit && edit(processor.getState())
    },
    [decorators, setDecorators, processor, isActive, edit],
  )
  const reset = React.useCallback(() => {
    setDecorators([])
  }, [setDecorators])

  return {
    getList: () => decorators,
    toggle,
    isActive,
    reset,
  }
}

export default useDecorators

function getSelectedDecorators(
  processor: ITextProcessor,
  filterDecorators: (s: ISlice) => boolean,
) {
  const selection = processor.getSelection()
  return processor
    .getSlices()
    .filter(
      (s) =>
        isRangeAIncludesB(s.range, selection) &&
        (selection.length || s.range.start !== selection.start) &&
        filterDecorators(s),
    )
    .map((s) => ({ type: s.type, data: s.data, id: s.id }))
}
